import {
  Typography,
  Box,
  CircularProgress,
  Card,
  CardContent,
  Paper,
  Grid2,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import {
  ArrowBack,
  Clear,
  ContentCopy,
  Image,
  Search,
  Shortcut,
} from "@mui/icons-material";
import {
  Job,
  colorByStatus,
  statusToLabel,
  BrandBatchSettings,
} from "../../../pages/playground/types";
import usePhrasebookStore from "./store";
import { keywordsSearch, Phrase, PhraseType } from "../../../api/phrasebook";
import { useEffect, useRef, useState } from "react";
import { getPhraseSections } from "../../../utils/fieldNames";
import FormattedText from "../../common/formatted-text";

export default function EditorPhrasebookSearchJobs({
  onCopyPhrases,
  batchSettings,
}: {
  onCopyPhrases: (
    type: PhraseType,
    phrases: Phrase[],
    append?: boolean
  ) => void;
  batchSettings?: BrandBatchSettings;
}) {
  const [jobsLoading, setJobsLoading] = useState<boolean>(false);

  const [jobs, setJobs] = useState<Job[]>([]);

  const {
    batchId,
    activeField,
    query,
    setQuery,
    garmentType,
    startPhrasebookSearch,
    clearSearch,
  } = usePhrasebookStore();

  const [startingQuery] = useState<string>(query);
  const abortControllerRef = useRef<AbortController | null>(null);
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Clear any existing timeouts
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    // Abort any ongoing requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create a new abort controller for this request
    abortControllerRef.current = new AbortController();

    // Keep showing existing jobs but limit them during loading for better UX
    setJobsLoading(true);
    if (Array.isArray(jobs)) {
      setJobs([...jobs.slice(0, 5)]);
    }

    // Add debounce to avoid excessive requests during typing
    searchTimeout.current = setTimeout(() => {
      keywordsSearch({
        batchId,
        query,
        scope: "brand",
        signal: abortControllerRef.current?.signal,
      })
        .then((response) => {
          const data = response.data.data;
          if (Array.isArray(data)) {
            setJobs(data);
          } else {
            console.error("Expected array but got:", data);
            setJobs([]);
          }
        })
        .catch((e) => {
          // Don't show errors for aborted requests
          if (e.name !== "AbortError" && e.name !== "CanceledError") {
            console.error("Error searching phrases by keywords:", e);
          }
          setJobs([]);
        })
        .finally(() => {
          setJobsLoading(false);
        });
    }, 300); // 300ms debounce

    // Cleanup function to abort request when component unmounts or dependencies change
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [query, batchId]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        p: 2,
        height: "100%",
      }}
      role="tabpanel"
      id="jobs-panel"
      aria-labelledby="jobs-tab"
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: 2,
          mb: "1rem",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
          }}
        >
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            label="Search by keywords in similar SKUs"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
            placeholder="e.g. Men's shirt"
          />
        </Box>
        {activeField ? (
          <ArrowBack
            onClick={() => {
              startPhrasebookSearch(activeField, startingQuery);
            }}
            sx={{
              fontSize: "1.5rem",
              color: "black",
              cursor: "pointer",
            }}
          />
        ) : (
          <Clear
            onClick={() => {
              clearSearch();
            }}
            sx={{
              fontSize: "1.5rem",
              color: "black",
              cursor: "pointer",
            }}
          />
        )}
      </Box>

      {jobsLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!jobsLoading && jobs.length === 0 && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: "center" }}
        >
          There are no similar SKUs in this batch
          {query !== "" ? ` for keywords "${query}"` : "."}
        </Typography>
      )}

      {!jobsLoading && jobs.length > 0 && (
        <>
          <Grid2 container spacing={2}>
            {jobs.map((job) => {
              // Get phrase sections with custom labels
              const phraseSections = getPhraseSections(batchSettings);

              // Build sections with content
              const sectionsWithContent = phraseSections.map((section) => ({
                ...section,
                content: job.phrases.filter(
                  (phrase) => phrase.type === section.type
                ),
              }));

              return (
                <Card key={job.id} variant="outlined" sx={{ width: "100%" }}>
                  <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          backgroundColor: "grey.100",
                          color: "text.primary",
                          fontSize: "0.8rem",
                          py: 0.3,
                          px: 0.8,
                          borderRadius: 1,
                          display: "flex",
                          alignItems: "center",
                          fontWeight: 600,
                        }}
                      >
                        {job.master_id}
                      </Box>

                      {job.garment_type && (
                        <Box
                          component="span"
                          sx={{
                            backgroundColor:
                              garmentType === job.garment_type
                                ? "success.light"
                                : "grey.100",
                            color:
                              garmentType === job.garment_type
                                ? "success.contrastText"
                                : "text.primary",
                            fontSize: "0.75rem",
                            py: 0.3,
                            px: 0.8,
                            borderRadius: 1,
                            display: "flex",
                            alignItems: "center",
                            fontWeight: 500,
                          }}
                        >
                          {job.garment_type}
                        </Box>
                      )}

                      <Box
                        component="span"
                        sx={{
                          backgroundColor: colorByStatus[job.status] || "grey.100",
                          color: "white",
                          fontSize: "0.75rem",
                          py: 0.3,
                          px: 0.8,
                          borderRadius: 1,
                          display: "flex",
                          alignItems: "center",
                          fontWeight: 500,
                        }}
                      >
                        {statusToLabel(job.status)}
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        gap: 2,
                      }}
                    >
                      <Box
                        sx={{
                          flex: 4,
                        }}
                      >
                        {sectionsWithContent.map(
                          (section, idx) =>
                            section.content.length > 0 && (
                              <Box key={idx} sx={{ mb: 2 }}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    mb: 0.5,
                                  }}
                                >
                                  <IconButton
                                    size="small"
                                    title={`Copy ${section.label}`}
                                    onClick={() => {
                                      onCopyPhrases(
                                        section.type,
                                        job.phrases.filter(
                                          (phrase) =>
                                            phrase.type === section.type
                                        )
                                      );
                                    }}
                                  >
                                    <Shortcut
                                      fontSize="small"
                                      sx={{
                                        color: "text.primary",
                                      }}
                                    />
                                  </IconButton>

                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <Typography
                                      variant="subtitle2"
                                      sx={{ ml: 0.5, fontWeight: "bold" }}
                                    >
                                      {section.label}
                                    </Typography>
                                  </Box>
                                </Box>
                                <Paper
                                  variant="outlined"
                                  sx={{ p: 1, backgroundColor: "grey.50" }}
                                >
                                  {section.content.length > 0 ? (
                                    section.content.map((phrase, phraseIdx) => (
                                      <Box
                                        key={phraseIdx}
                                        sx={{
                                          display: "flex",
                                          justifyContent: "space-between",
                                          alignItems: "center",
                                          py: 0.5,
                                          px: 0.5,
                                          borderRadius: 1,
                                          "&:hover": {
                                            backgroundColor:
                                              "rgba(0, 0, 0, 0.04)",
                                          },
                                        }}
                                      >
                                        <IconButton
                                          size="small"
                                          onClick={() => {
                                            onCopyPhrases(
                                              section.type,
                                              [phrase],
                                              true
                                            );
                                          }}
                                        >
                                          <ContentCopy
                                            sx={{
                                              fontSize: "small",
                                              color: "text.primary",
                                            }}
                                          />
                                        </IconButton>
                                        <Typography
                                          variant="body2"
                                          component="div"
                                          sx={{
                                            whiteSpace: "pre-wrap",
                                            flexGrow: 1,
                                          }}
                                        >
                                          <FormattedText text={phrase.phrase} />
                                        </Typography>
                                      </Box>
                                    ))
                                  ) : (
                                    <Typography
                                      variant="body2"
                                      sx={{ whiteSpace: "pre-wrap" }}
                                    >
                                      No phrases available
                                    </Typography>
                                  )}
                                </Paper>
                              </Box>
                            )
                        )}
                      </Box>

                      <Box
                        sx={{
                          flex: 2,
                          display: "flex",
                          flexDirection: "column",
                          height: "100%",
                        }}
                      >
                        {job.images && job.images.length > 0 ? (
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: "repeat(2, 1fr)", // Fixed 2 columns
                              gridTemplateRows: "repeat(2, 1fr)", // Fixed 2 rows
                              gap: 1,
                              height: "100%",
                              padding: 1,
                              backgroundColor: "grey.50",
                              borderRadius: 1,
                            }}
                          >
                            {job.images
                              .slice(0, 6)
                              .map((img: string, idx: number) => (
                                <Box
                                  key={idx}
                                  sx={{
                                    position: "relative",
                                    aspectRatio: "1/1",
                                    borderRadius: 1,
                                    overflow: "hidden",
                                    boxShadow: 1,
                                    backgroundColor: "white",
                                  }}
                                >
                                  <Box
                                    component="img"
                                    src={img}
                                    sx={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "contain",
                                    }}
                                    alt={`Product ${idx + 1}`}
                                  />
                                </Box>
                              ))}
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              height: "100%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: "grey.100",
                              borderRadius: 1,
                              padding: 2,
                            }}
                          >
                            <Image color="disabled" sx={{ fontSize: 48 }} />
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Grid2>
        </>
      )}
    </Box>
  );
}
